/* eslint-disable react/no-danger */
// disabled because its handled properly in the hook by using DOMPurify
import React from "react";
import DOMPurify from "dompurify";

type TAs = "p" | "span";

/**
 * - this is a hook that provides a **safe** way to use dangerouslySetInnerHTML for dynamic string interpolation content in localization scenarios.
 * - it provides a `SanitizedInnerHTMLRoot` component that can be used to render the sanitized content.
 * - and a `createInnerHTMLString` function that can be used to quickly create a valid html string that supports content and even a className.
 * @example
 *
 * import { K, i18n, useDangerousHTMLSafely } from "@locales";
 * import { makeStyles } from "@fluentui/react-components";
 *
 * const useClasses = makeStyles({
 *    testClass: {
 *      fontWeight: "bold",
 *   },
 * });
 *
 * function MyComponent() {
 *  const classes = useClasses();
 *  const { Root, createInnerHTMLString } = useDangerousHTMLSafely();
 *
 *  const someTextThatIsBold = createInnerHTMLString(
 *    "My Dynamic Text",
 *    classes.testClass,
 *  );
 *
 *  return (
 *   <span>
 *     // root can also get a `as` prop to render a p tag instead of a span
 *     // or receive a className prop to add a class to the rendered element
 *     <SanitizedInnerHTMLRoot>
 *        {i18n.tInter(K.assets.go_to_slide({ position: someTextThatIsBold }))}
 *     </SanitizedInnerHTMLRoot>
 *   </span>
 *  );
 * }
 *
 * // note that spans inherit from spans, so when using a typical Typography component, you can use this as any other span
 * // so this would be equally valid in case of fluents Typography component
 *
 * return (
 *  <Typography preset="subtitle2">
 *   <SanitizedInnerHTMLRoot>
 *    {i18n.tInter(K.assets.go_to_slide({ position: someTextThatIsBold }))}
 *  </SanitizedInnerHTMLRoot>
 * </Typography>
 * );
 *
 *
 */
function useDangerousHTMLSafely() {
  function SanitizedInnerHTMLRoot({
    as = "span",
    className = undefined,
    children,
  }: {
    as?: TAs;
    className?: string;
    children: string;
  }) {
    const sanitized = DOMPurify.sanitize(children);

    return as === "p" ? (
      <p
        className={className}
        dangerouslySetInnerHTML={{ __html: sanitized }}
      />
    ) : (
      <span
        className={className}
        dangerouslySetInnerHTML={{ __html: sanitized }}
      />
    );
  }

  function createInnerHTMLString(
    content: string,
    className: string,
    as: TAs = "span",
  ) {
    return as === "p"
      ? `<p class="${className}">${content}</p>`
      : `<span class="${className}">${content}</span>`;
  }

  return {
    SanitizedInnerHTMLRoot,
    createInnerHTMLString,
  };
}

export { useDangerousHTMLSafely };
